<script>
/**
 * @component PatientPrescriptionRequest
 * @description Portal del paciente — solicitud de recetas de medicamentos.
 *              Muestra un formulario para seleccionar medicamento y solicitar receta,
 *              y el historial de solicitudes previas con sus estados.
 * @example <PatientPrescriptionRequest />
 */
import Spinner from "@/components/ui/Spinner";
import indicationService from "@/services/indication";
import medicationRequestService from "@/services/medicationRequest";

export default {
  name: "PatientPrescriptionRequest",

  components: { Spinner },

  data() {
    return {
      selectedMedicationId: "",
      prescriptions: [],
      isLoading: false,
      isSubmitting: false,
      selectedIndication: null,
      medicationRequests: [],
      isLoadingRequests: false,
    };
  },

  computed: {
    /**
     * @description Medicamentos únicos deduplicados de las indicaciones del paciente
     * @returns {Array}
     */
    uniqueMedications() {
      const medicationMap = new Map();
      const sorted = [...this.prescriptions].sort(
        (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
      );
      sorted.forEach((p) => {
        const med = p.medication || p.publicMedication;
        if (!med) return;
        const key = med._id || med.drugId;
        if (!key || medicationMap.has(key)) return;
        medicationMap.set(key, {
          id: key,
          name: med.name || `${med.brandName || ""} - ${med.drug || ""}`.trim(),
          presentation: med.presentation || "",
          indication: p,
        });
      });
      return Array.from(medicationMap.values());
    },

    /**
     * @description Opciones formateadas para el el-select
     * @returns {Array}
     */
    medicationOptions() {
      return this.uniqueMedications.map((med) => ({
        value: med.id,
        label: med.presentation
          ? `${med.name} - ${med.presentation}`
          : med.name,
        indication: med.indication,
      }));
    },

    /**
     * @description Label del medicamento actualmente seleccionado
     * @returns {string}
     */
    selectedMedicationLabel() {
      if (!this.selectedMedicationId) return "";
      const opt = this.medicationOptions.find(
        (o) => o.value === this.selectedMedicationId
      );
      return opt ? opt.label : "";
    },
  },

  async created() {
    await Promise.all([
      this.loadPrescriptions(),
      this.loadMedicationRequests(),
    ]);
  },

  methods: {
    /**
     * @description Carga las indicaciones de medicamentos del paciente
     */
    async loadPrescriptions() {
      this.isLoading = true;
      try {
        const patientId = this.$store.state.user._id;
        const indications = await indicationService.get(patientId, [
          { type: "medications" },
        ]);
        this.prescriptions = indications || [];
      } catch (error) {
        console.error("Error al cargar las prescripciones:", error);
        this.$message.error("Error al cargar las prescripciones");
      } finally {
        this.isLoading = false;
      }
    },

    /**
     * @description Callback al cambiar el medicamento seleccionado
     * @param {string} medicationId - ID del medicamento
     */
    onMedicationChange(medicationId) {
      if (!medicationId) {
        this.selectedIndication = null;
        return;
      }
      const match = this.prescriptions
        .filter((p) => {
          const med = p.medication || p.publicMedication;
          if (!med) return false;
          return (med._id || med.drugId) === medicationId;
        })
        .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
      this.selectedIndication = match[0] || null;
    },

    /**
     * @description Limpia la selección de medicamento
     */
    clearMedication() {
      this.selectedMedicationId = "";
      this.selectedIndication = null;
    },

    /**
     * @description Carga el historial de solicitudes del paciente
     */
    async loadMedicationRequests() {
      this.isLoadingRequests = true;
      try {
        const patientId = this.$store.state.user._id;
        const res = await medicationRequestService.get({ patientId });
        this.medicationRequests = res.docs || res || [];
      } catch (error) {
        console.error("Error al cargar solicitudes:", error);
        this.$message.error("Error al cargar las solicitudes de recetas");
      } finally {
        this.isLoadingRequests = false;
      }
    },

    /**
     * @description Nombre del medicamento de una solicitud
     * @param {Object} request
     * @returns {string}
     */
    getMedicationName(request) {
      return (
        request.medication?.name ||
        request.publicMedication?.name ||
        request.publicMedication?.brandName ||
        "Sin medicación"
      );
    },

    /**
     * @description Presentación/dosis del medicamento de una solicitud
     * @param {Object} request
     * @returns {string}
     */
    getMedicationPresentation(request) {
      return (
        request.medication?.presentation ||
        request.publicMedication?.presentation ||
        ""
      );
    },

    /**
     * @description Diagnóstico de una solicitud
     * @param {Object} request
     * @returns {string}
     */
    getDiagnosisName(request) {
      if (typeof request.diagnosis === "string") return request.diagnosis;
      if (request.diagnosis?.name) return request.diagnosis.name;
      if (typeof request.publicDiagnosis === "string")
        return request.publicDiagnosis;
      if (request.publicDiagnosis?.name) return request.publicDiagnosis.name;
      return "Sin diagnóstico";
    },

    /**
     * @description Texto legible del estado de una solicitud
     * @param {string} status
     * @returns {string}
     */
    getStatusText(status) {
      return (
        {
          pending: "Pendiente",
          approved: "Aprobada",
          rejected: "Rechazada",
          cancelled: "Cancelada",
        }[status] || status
      );
    },

    /**
     * @description Clase CSS del badge según estado
     * @param {string} status
     * @returns {string}
     */
    getStatusClass(status) {
      return (
        {
          pending: "badge--pending",
          approved: "badge--approved",
          rejected: "badge--rejected",
          cancelled: "badge--cancelled",
        }[status] || ""
      );
    },

    /**
     * @description Cancela una solicitud pendiente
     * @param {Object} request
     */
    async cancelRequest(request) {
      try {
        await this.$confirm(
          "¿Desea cancelar esta solicitud de medicamento?",
          "Cancelar solicitud",
          {
            confirmButtonText: "Cancelar solicitud",
            cancelButtonText: "No",
            type: "warning",
          }
        );
        await medicationRequestService.update(request._id, {
          status: "cancelled",
        });
        const idx = this.medicationRequests.findIndex(
          (r) => r._id === request._id
        );
        if (idx !== -1)
          this.$set(this.medicationRequests[idx], "status", "cancelled");
        this.$message({ type: "success", message: "Solicitud cancelada" });
      } catch (error) {
        if (error !== "cancel") {
          console.error("Error cancelando solicitud:", error);
          this.$message({
            type: "error",
            message: "Error al cancelar la solicitud",
          });
        }
      }
    },

    /**
     * @description Envía la solicitud de receta
     */
    async handleRequestPrescription() {
      if (!this.selectedMedicationId) {
        this.$message.warning("Por favor seleccione un medicamento");
        return;
      }
      if (!this.selectedIndication) {
        this.$message.warning(
          "No se encontró la información de la prescripción"
        );
        return;
      }
      this.isSubmitting = true;
      try {
        await medicationRequestService.create({
          patientId: this.$store.state.user._id,
          ...this.selectedIndication,
        });
        this.$message.success("Solicitud de receta enviada correctamente");
        this.clearMedication();
        await this.loadMedicationRequests();
      } catch (error) {
        console.error("Error al enviar la solicitud:", error);
        this.$message.error(
          error.response?.data?.error ||
            "Error al enviar la solicitud de receta"
        );
      } finally {
        this.isSubmitting = false;
      }
    },
  },
};
</script>

<template lang="pug">
.rx-page

  //- ── Nueva solicitud ──────────────────────────────────
  .rx-card
    .rx-card__header
      .rx-card__icon
        micon(name="add_circle")
      span.rx-card__title Nueva solicitud de receta

    .rx-card__body
      .rx-form

        //- Banner informativo
        .rx-info-banner
          micon(name="info")
          span Tu médico revisará la solicitud y emitirá la receta. Recibirás una notificación cuando esté lista.

        //- Selector / pill de medicamento
        .rx-field
          label.rx-label Medicamento

          //- Pill cuando ya hay selección
          .rx-med-pill(v-if="selectedMedicationId")
            micon(name="medication")
            span.rx-med-pill__name {{ selectedMedicationLabel }}
            button.rx-med-pill__remove(@click="clearMedication" title="Cambiar")
              micon(name="clear")

          //- Selector cuando no hay selección
          .rx-select-wrap(v-else)
            el-select.rx-select(
              v-model="selectedMedicationId"
              filterable
              clearable
              placeholder="Seleccionar medicamento..."
              :loading="isLoading"
              @change="onMedicationChange"
            )
              el-option(
                v-for="opt in medicationOptions"
                :key="opt.value"
                :label="opt.label"
                :value="opt.value"
              )

        //- Footer del form
        .rx-form-footer
          span.rx-hint
            micon(name="schedule")
            | Tiempo de respuesta estimado: 24-48 hs
          el-button.rx-btn-submit(
            type="primary"
            :loading="isSubmitting"
            :disabled="!selectedMedicationId || isSubmitting"
            @click="handleRequestPrescription"
          )
            micon(name="send")
            | Solicitar receta

  //- ── Historial ────────────────────────────────────────
  .rx-card
    .rx-card__header
      .rx-card__icon.rx-card__icon--history
        micon(name="history")
      span.rx-card__title Mis solicitudes
      span.rx-count(v-if="medicationRequests.length") {{ medicationRequests.length }}

    //- Loading
    .rx-loading(v-if="isLoadingRequests")
      spinner

    //- Empty state
    .rx-empty(v-else-if="!medicationRequests.length")
      micon(name="receipt_long")
      p.rx-empty__title No tenés solicitudes todavía
      p.rx-empty__sub Cuando solicites una receta aparecerá aquí

    //- Tabla
    table.rx-table(v-else)
      thead
        tr
          th Medicamento
          th Diagnóstico
          th Estado
          th Fecha de solicitud
          th
      tbody
        tr(v-for="request in medicationRequests" :key="request._id")
          td
            .rx-med-name {{ getMedicationName(request) }}
            .rx-med-dose(v-if="getMedicationPresentation(request)") {{ getMedicationPresentation(request) }}
          td
            span.rx-diag {{ getDiagnosisName(request) }}
          td
            span.rx-badge(:class="getStatusClass(request.status)") {{ getStatusText(request.status) }}
          td
            span.rx-date {{ request.createdAt | formatDateAndTime }}
          td
            button.rx-cancel-btn(
              v-if="request.status === 'pending'"
              @click.stop="cancelRequest(request)"
              title="Cancelar solicitud"
            )
              micon(name="clear")
            span.rx-cancel-btn.rx-cancel-btn--disabled(v-else)
              micon(name="clear")
</template>

<style lang="scss" scoped>
.rx-page {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

// ── Card ──────────────────────────────────────────────
.rx-card {
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  overflow: hidden;

  &__header {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 16px 24px;
    border-bottom: 1px solid #f1f5f9;
  }

  &__icon {
    width: 32px;
    height: 32px;
    background: #eef2ff;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;

    .micon {
      width: 18px;
      height: 18px;
      fill: #4f46e5;
    }

    &--history {
      background: #f0fdf4;
      .micon {
        fill: #16a34a;
      }
    }
  }

  &__title {
    font-size: 13px;
    font-weight: 700;
    color: #1e3a5f;
  }

  &__body {
    padding: 20px 24px;
  }
}

.rx-count {
  background: #f1f5f9;
  color: #64748b;
  font-size: 11px;
  font-weight: 700;
  padding: 2px 8px;
  border-radius: 20px;
}

// ── Form ──────────────────────────────────────────────
.rx-form {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.rx-info-banner {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  background: #f0f9ff;
  border: 1px solid #bae6fd;
  border-radius: 10px;
  padding: 12px 16px;
  font-size: 12px;
  color: #0369a1;
  line-height: 1.5;

  .micon {
    width: 17px;
    height: 17px;
    fill: #0ea5e9;
    flex-shrink: 0;
    margin-top: 1px;
  }
}

.rx-field {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.rx-label {
  font-size: 12px;
  font-weight: 600;
  color: #64748b;
}

// Wrapper + override del el-select de Element UI
.rx-select-wrap {
  width: 100%;
}

.rx-select {
  width: 100%;

  ::v-deep .el-input__inner {
    border: 1px solid #e2e8f0;
    border-radius: 10px;
    background: #f8fafc;
    font-size: 13px;
    color: #1e293b;
    height: 42px;
    transition: border-color 0.15s, background 0.15s;

    &::placeholder {
      color: #94a3b8;
    }
    &:focus {
      border-color: #1e3a5f;
      background: #fff;
    }
  }

  ::v-deep .el-input.is-focus .el-input__inner {
    border-color: #1e3a5f;
    background: #fff;
  }

  ::v-deep .el-input__suffix {
    right: 10px;
  }
}

// Pill de medicamento seleccionado
.rx-med-pill {
  display: flex;
  align-items: center;
  gap: 10px;
  background: #eef2ff;
  border-radius: 10px;
  padding: 10px 14px;

  .micon {
    width: 17px;
    height: 17px;
    fill: #a5b4fc;
    flex-shrink: 0;
  }

  &__name {
    flex: 1;
    font-size: 13px;
    font-weight: 600;
    color: #4f46e5;
  }

  &__remove {
    background: none;
    border: none;
    cursor: pointer;
    padding: 2px;
    display: flex;
    align-items: center;

    .micon {
      fill: #a5b4fc;
      transition: fill 0.15s;
    }

    &:hover .micon {
      fill: #4f46e5;
    }
  }
}

// Footer del form
.rx-form-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-top: 4px;
}

.rx-hint {
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 12px;
  color: #94a3b8;

  .micon {
    width: 15px;
    height: 15px;
    fill: #cbd5e1;
  }
}

.rx-btn-submit {
  display: flex !important;
  align-items: center;
  gap: 6px;
  background: #1e3a5f !important;
  border-color: #1e3a5f !important;
  border-radius: 8px !important;
  font-size: 13px !important;
  font-weight: 600 !important;
  padding: 10px 20px !important;

  .micon {
    width: 16px;
    height: 16px;
    fill: #fff;
  }

  &:hover {
    background: #162d4a !important;
  }

  &.is-disabled {
    opacity: 0.4;
    cursor: not-allowed !important;
  }
}

// ── Loading / empty ────────────────────────────────────
.rx-loading {
  padding: 40px;
  text-align: center;
}

.rx-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 48px 24px;
  gap: 8px;

  .micon {
    width: 40px;
    height: 40px;
    fill: #e2e8f0;
  }

  &__title {
    font-size: 14px;
    font-weight: 600;
    color: #64748b;
  }

  &__sub {
    font-size: 13px;
    color: #94a3b8;
  }
}

// ── Tabla historial ────────────────────────────────────
.rx-table {
  width: 100%;
  border-collapse: collapse;

  th {
    text-align: left;
    padding: 10px 16px;
    font-size: 11px;
    font-weight: 700;
    color: #94a3b8;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    border-bottom: 1px solid #f1f5f9;
  }

  td {
    padding: 14px 16px;
    border-bottom: 1px solid #f8fafc;
    vertical-align: middle;
  }

  tr:last-child td {
    border-bottom: none;
  }
  tr:hover td {
    background: #fafbfc;
  }
}

.rx-med-name {
  font-weight: 600;
  font-size: 13px;
  color: #1e293b;
}

.rx-med-dose {
  font-size: 12px;
  color: #94a3b8;
  margin-top: 2px;
}

.rx-diag {
  font-size: 13px;
  color: #475569;
}

.rx-date {
  font-size: 12px;
  color: #64748b;
  white-space: nowrap;
}

// Badges de estado
.rx-badge {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 4px 10px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;

  &::before {
    content: "";
    width: 6px;
    height: 6px;
    border-radius: 50%;
    flex-shrink: 0;
  }

  &.badge--pending {
    background: #fff7ed;
    color: #ea580c;
    &::before {
      background: #ea580c;
    }
  }
  &.badge--approved {
    background: #f0fdf4;
    color: #16a34a;
    &::before {
      background: #16a34a;
    }
  }
  &.badge--rejected {
    background: #fef2f2;
    color: #dc2626;
    &::before {
      background: #dc2626;
    }
  }
  &.badge--cancelled {
    background: #f8fafc;
    color: #94a3b8;
    &::before {
      background: #94a3b8;
    }
  }
}

// Botón cancelar
.rx-cancel-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  border-radius: 7px;
  border: 1px solid #e2e8f0;
  background: #fff;
  cursor: pointer;
  transition: all 0.15s;

  .micon {
    width: 15px;
    height: 15px;
    fill: #94a3b8;
  }

  &:hover {
    border-color: #fca5a5;
    background: #fef2f2;
    .micon {
      fill: #dc2626;
    }
  }

  &--disabled {
    opacity: 0.25;
    cursor: default;
    pointer-events: none;
  }
}

@media (max-width: 767px) {
  .rx-card__header {
    padding: 14px 16px;
  }
  .rx-card__body {
    padding: 16px;
  }

  .rx-form-footer {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }

  .rx-btn-submit {
    width: 100% !important;
    justify-content: center;
  }

  .rx-table {
    th:nth-child(2),
    td:nth-child(2) {
      display: none;
    }
  }
}
</style>
